import React, { useCallback, useEffect, useState } from 'react'
import { useField, useFormikContext } from 'formik'
import { Box, InputLabel, TextField as MuiTextField } from '@material-ui/core'
import { useFieldError, useFieldFocused } from '~/common/hooks'
import { useTranslation } from '@opus/web.core.hooks.use-translation'
import { phoneFieldStyle, useStyles } from './phone-field.style'
import NumberFormat from 'react-number-format'
import { compose, phone, required } from '~/common/validator'
import { FIELD_MODE } from '~/common/constants'
import { LabelField } from '@opus/web.core.form.label-field'
import { useDebounce } from 'react-use'
import { defaultTo } from 'lodash'
import { observer } from '~/common/mobx.decorator'

const PHONE_FORMAT = '(###) ###-####'
const PHONE_CODE = '+1'
const PhoneFormat = observer(({ inputRef, onChange, value, ...rest }) => {
	const [inputValue, setInputValue] = useState(value)

	const onValueChange = useCallback((values) => {
		setInputValue(values.value || '')
	}, [])

	useEffect(() => {
		if (value !== inputValue) {
			setInputValue(value)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value])

	useDebounce(
		() => {
			onChange({ target: { name: rest.name, value: inputValue } })
		},
		300,

		[inputValue]
	)

	return (
		<NumberFormat
			{...rest}
			value={defaultTo(inputValue, value)}
			defaultValue={PHONE_CODE}
			getInputRef={inputRef}
			onValueChange={onValueChange}
			format={PHONE_FORMAT}
			isNumericString
		/>
	)
})

export const PhoneField = observer(
	({
		name,
		validate = () => {},
		label,
		placeholder,
		mode = FIELD_MODE.edit,
		showLabel = false,
		showError = true,
		onChange,
		debounceTime = 500,
		endLabel,
		...rest
	}) => {
		const { t } = useTranslation()
		const formik = useFormikContext()
		const classes = useStyles()

		const fieldProps = name ? { name, validate: mode === FIELD_MODE.edit && validate && showError } : { name: '', validate: () => {}, value: null }
		const [field, meta] = useField(fieldProps)
		const [internalValue, setInternalValue] = useState(field?.value?.replace(PHONE_CODE, '') || '')
		const [internalError, setInternalError] = useState(null)

		const formikError = useFieldError(meta)

		const isFormikContext = !!formik

		const [focused, focusProps] = useFieldFocused(isFormikContext ? field : { onBlur: () => {} })

		useEffect(() => {
			if (isFormikContext && field?.value) {
				setInternalValue(field.value.replace(PHONE_CODE, ''))
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [isFormikContext, field?.value])

		const handleChange = useCallback(
			(event) => {
				const newValue = event.target.value ? `${PHONE_CODE}${event.target.value}` : ''
				setInternalValue(event.target.value)

				if (!isFormikContext) {
					const validationError = validate(newValue)
					setInternalError(validationError || '')
				}

				// Get fields that useful for the `Profile Completion` feature
				const { id, name, type, 'data-name': dataName } = rest || {}

				const payload = {
					target: {
						id,
						name,
						value: newValue,
						type,
						'data-name': dataName,
						quizQuestionId: id,
					},
				}

				if (onChange) {
					onChange(payload)
				}
			},
			[isFormikContext, validate, onChange, rest]
		)

		useDebounce(
			() => {
				if (isFormikContext && field?.value !== `${PHONE_CODE}${internalValue}` && !focused) {
					field.onChange({ target: { name, value: `${PHONE_CODE}${internalValue}` } })
				}
			},
			debounceTime,
			[internalValue, focused, isFormikContext]
		)

		const displayValue = internalValue
		const error = isFormikContext ? formikError : internalError

		if (mode === FIELD_MODE.view) {
			return <LabelField label={t(label)} displayValueFormat={() => <NumberFormat value={displayValue} displayType="text" format={PHONE_FORMAT} />} />
		}

		return (
			<>
				<Box display="flex" justifyContent="space-between">
					{label && (
						<InputLabel shrink htmlFor={name} className={classes.label}>
							{t(label)}
						</InputLabel>
					)}
					{endLabel && endLabel}
				</Box>
				<MuiTextField
					id={name}
					label={showLabel ? t(label) : null}
					placeholder={placeholder ? t(placeholder) : PHONE_FORMAT}
					helperText={mode === FIELD_MODE.edit && error && showError ? error : undefined}
					error={mode === FIELD_MODE.edit && !!error}
					css={phoneFieldStyle}
					InputProps={{
						inputComponent: PhoneFormat,
						...focusProps,
					}}
					InputLabelProps={{ shrink: focused || !!displayValue || mode === FIELD_MODE.view }}
					{...(isFormikContext ? field : {})}
					value={displayValue}
					onChange={handleChange}
					{...rest}
				/>
			</>
		)
	}
)

PhoneField.defaultProps = {
	label: 'PHONE_NUMBER',
	validate: compose(required, phone),
	mode: FIELD_MODE.edit,
}
